import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOpenOperationRow } from './OperationNav';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import type { HintRequest } from '../../types';
import { DataTable, type ColumnDef, type FilterDef } from '@shared-ui/components';
import { LatexText } from '@shared-ui/latex';
import { buildQuestionDisplayText } from '@padcanvas/shared';
import { OperationsFilterBar } from './OperationsFilterBar';
import { operationsRowMenuItems, AGENTS_FILTER_DEF, DEFAULT_AGENTS, DEFAULT_DATE_RANGE, getSinceDate, syncOperationsFiltersToUrl, readOperationsParam, userSuggestionsFromRows } from './operationsFilters';

const pageSize = 20;

const formatLatency = (ms?: number) => (ms ? `${(ms / 1000).toFixed(1)}s` : '-');

type HintFilters = {
  dateRange: string;
  subject: string;
  status: string;
  agents: string;
  userId: string;
};

const STATUS_OPTIONS = [
  { value: 'generating', label: 'Generating' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

export function HintList() {
  const openRow = useOpenOperationRow();
  const [searchParams, setSearchParams] = useSearchParams();
  const OP = 'hints'; // URL param namespace so operation tabs don't collide

  const [page, setPage] = useState(Number(readOperationsParam(searchParams, 'page', OP)) || 1);

  const [appliedFilters, setAppliedFilters] = useState<HintFilters>({
    dateRange: readOperationsParam(searchParams, 'dateRange', OP) || DEFAULT_DATE_RANGE,
    subject: readOperationsParam(searchParams, 'subject', OP) || '',
    status: readOperationsParam(searchParams, 'status', OP) || '',
    agents: readOperationsParam(searchParams, 'agents', OP) || DEFAULT_AGENTS,
    userId: readOperationsParam(searchParams, 'userId', OP) || '',
  });

  const extraDefs = useMemo<FilterDef[]>(() => [
    { key: 'status', kind: 'select', placeholder: 'All Statuses', options: STATUS_OPTIONS },
    AGENTS_FILTER_DEF,
  ], []);

  const handleFiltersChange = (values: Record<string, string>) => {
    const next = { ...appliedFilters, ...(values as Partial<HintFilters>) };
    setAppliedFilters(next);
    setPage(1);
    syncOperationsFiltersToUrl(setSearchParams, next, 1, OP);
  };

  // User only: these lists carry no question filter, and offering one they
  // cannot apply would be a menu item that does nothing.
  const rowMenu = (h: HintRequest) => operationsRowMenuItems({
    userId: h.user?.id,
    userLabel: h.user?.email || h.user?.name,
    appliedUserId: appliedFilters.userId,
    apply: next => {
      const updated = { ...appliedFilters, ...next };
      setAppliedFilters(updated);
      setPage(1);
      syncOperationsFiltersToUrl(setSearchParams, updated, 1, OP);
    },
  });

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    syncOperationsFiltersToUrl(setSearchParams, appliedFilters, newPage, OP);
  };

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['hintRequests', page, appliedFilters],
    queryFn: () => adminApi.getHintRequests({
      page,
      pageSize,
      subject: appliedFilters.subject || undefined,
      status: appliedFilters.status || undefined,
      userId: appliedFilters.userId || undefined,
      agents: appliedFilters.agents || undefined,
      sinceDate: getSinceDate(appliedFilters.dateRange),
    }),
  });

  const rows = data?.hintRequests || [];
  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  const userSuggestions = useMemo(() => userSuggestionsFromRows(rows), [rows]);

  const handleUserChange = (userId: string) => {
    const next = { ...appliedFilters, userId };
    setAppliedFilters(next);
    setPage(1);
    syncOperationsFiltersToUrl(setSearchParams, next, 1, OP);
  };

  const columns: ColumnDef<HintRequest>[] = useMemo(() => [
    {
      key: 'date',
      header: 'Date',
      className: 'dt-col-date',
      render: (h) => (
        <div className="dc-stack">
          <span className="dc-date">{format(new Date(h.createdAt), 'MMM d, HH:mm')}</span>
          {h.status === 'generating' && (
            <span className="dc-status dc-status--analyzing" title="Hint generation in flight">GENERATING</span>
          )}
          {h.status === 'failed' && (
            <span className="dc-status dc-status--failed" title="Most recent hint turn failed">FAILED</span>
          )}
        </div>
      ),
    },
    {
      key: 'user',
      header: 'User',
      render: (h) => (
        <div className="dc-stack">
          <span className="dc-name">{h.user?.name || 'Unknown'}</span>
          <span className="dc-email">{h.user?.email || '-'}</span>
        </div>
      ),
    },
    {
      key: 'subject',
      header: 'Subject',
      className: 'dt-col-subject',
      render: (h) => {
        const subj = h.question?.subject || '';
        return (
          <div className="dc-stack">
            <span className={`dc-subject ${subj ? `dc-subject--${subj}` : ''}`}>{subj || '-'}</span>
            <span className="dc-topic">{h.question?.topic || '-'}</span>
          </div>
        );
      },
    },
    {
      key: 'question',
      header: 'Question',
      render: (h) => h.question?.questionText
        ? <LatexText text={buildQuestionDisplayText(h.question)} truncate={100} />
        : <span className="dc-no-value">-</span>,
    },
    {
      key: 'provider',
      header: 'Provider',
      render: (h) => <span className="provider-badge">{h.llmProvider}</span>,
    },
    {
      key: 'input',
      header: 'Input',
      render: (h) => <span className="dc-tag">{h.inputMode || '-'}</span>,
    },
    {
      key: 'latency',
      header: 'Latency',
      render: (h) => formatLatency(h.latencyMs),
    },
  ], []);

  if (error) return <div className="error">Error: {(error as Error).message}</div>;

  return (
    <>
      <OperationsFilterBar
        values={appliedFilters as unknown as Record<string, string>}
        onChange={handleFiltersChange}
        extraDefs={extraDefs}
        onUserChange={handleUserChange}
        userSuggestions={userSuggestions}
        loading={isFetching}
      />

      <DataTable
        columns={columns}
        data={rows}
        rowKey={(h) => h.id}
        onRowClick={(h) => openRow(rows, h, '/hints', { page })}
        rowHref={(h) => `/hints/${h.id}`}
        rowMenu={rowMenu}
        pagination={{ page, totalPages, total: data?.total, onPageChange: handlePageChange }}
        loading={isLoading}
        emptyMessage="No hint requests yet."
      />
    </>
  );
}
